'use client'

import {
  Description,
  FieldError,
  Input as HeroInput,
  InputGroup,
  Label,
  TextField
} from '@heroui/react'
import { forwardRef, ReactNode, type ChangeEvent, type ComponentPropsWithoutRef } from 'react'

import type { IconName } from '@/config/icons'
import { Icon } from '../icon'

/**
 * v3-correct, v2-compatible single-line text field.
 *
 * HeroUI v3 `Input` is a bare primitive: it has NO `label`, `isRequired`,
 * `errorMessage`, or `startContent` prop, so a v2-style `<Input label=...>`
 * silently renders with no label and no error text. This wrapper accepts the
 * v2 API the codebase already passes and renders the correct v3 composition
 * (`TextField > Label + Input/InputGroup + FieldError`), so callers only need
 * to swap their import.
 *
 * onChange keeps the historical `{ target: { name, value } }` shape so both
 * react-hook-form `Controller` fields (spread `{...field}`) and `register`
 * consumers keep working unchanged.
 */
type InputBaseAttrs = Omit<
  ComponentPropsWithoutRef<'input'>,
  'onChange' | 'value' | 'defaultValue' | 'size' | 'color' | 'prefix'
>

export interface InputProps extends InputBaseAttrs {
  label?: ReactNode
  value?: string
  defaultValue?: string
  /** Receives a synthetic change event whose `target.value` is the new string. */
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void
  isRequired?: boolean
  required?: boolean
  isInvalid?: boolean
  isDisabled?: boolean
  isReadOnly?: boolean
  errorMessage?: ReactNode
  error?: ReactNode
  description?: ReactNode
  helperText?: ReactNode
  icon?: IconName
  startContent?: ReactNode
  endContent?: ReactNode
  /** Class applied to the inner input element (not the field container) */
  inputClassName?: string
  fullWidth?: boolean
  variant?: 'primary' | 'secondary'
  // v2-only props accepted and intentionally swallowed (no v3 equivalent)
  size?: 'sm' | 'md' | 'lg'
  labelPlacement?: string
  isClearable?: boolean
  onClear?: () => void
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    label,
    placeholder,
    type = 'text',
    name,
    value,
    defaultValue,
    onChange,
    onBlur,
    isRequired,
    required,
    isInvalid,
    isDisabled,
    disabled,
    isReadOnly,
    errorMessage,
    error,
    description,
    helperText,
    icon,
    startContent,
    endContent,
    className,
    inputClassName,
    fullWidth = true,
    min,
    max,
    step,
    variant,
    'aria-label': ariaLabel,
    // swallow v2-only props so they never reach the DOM (ignored via ...rest sibling)
    size,
    labelPlacement,
    isClearable,
    onClear,
    ...rest
  },
  ref
) {
  const errMsg = errorMessage ?? error
  const invalid = isInvalid ?? error != null
  const desc = description ?? helperText
  const prefix = startContent ?? (icon ? <Icon icon={icon} /> : undefined)
  const suffix = endContent
  const useGroup = prefix != null || suffix != null

  // TextField is controlled; it emits a plain string. Re-shape to the
  // historical event so react-hook-form and other consumers keep working.
  const handleChange = onChange
    ? (v: string) =>
        onChange({ target: { name, value: v } } as unknown as ChangeEvent<HTMLInputElement>)
    : undefined

  const inputProps = {
    placeholder,
    type,
    onBlur,
    min: min as never,
    max: max as never,
    step: step as never,
    variant,
    className: inputClassName,
    ...rest
  }

  return (
    <TextField
      className={[fullWidth ? 'w-full' : null, className].filter(Boolean).join(' ') || undefined}
      name={name}
      value={value}
      defaultValue={defaultValue}
      onChange={handleChange}
      isRequired={isRequired ?? required}
      isInvalid={invalid}
      isDisabled={isDisabled ?? disabled}
      isReadOnly={isReadOnly}
      aria-label={!label ? ariaLabel : undefined}
    >
      {label ? <Label>{label}</Label> : null}
      {useGroup ? (
        <InputGroup variant={variant} fullWidth={fullWidth}>
          {prefix != null ? <InputGroup.Prefix>{prefix}</InputGroup.Prefix> : null}
          <InputGroup.Input ref={ref} {...inputProps} />
          {suffix != null ? <InputGroup.Suffix>{suffix}</InputGroup.Suffix> : null}
        </InputGroup>
      ) : (
        <HeroInput ref={ref} {...inputProps} />
      )}
      {invalid && errMsg ? (
        <FieldError>{errMsg}</FieldError>
      ) : desc ? (
        <Description>{desc}</Description>
      ) : null}
    </TextField>
  )
})

Input.displayName = 'Input'

export default Input
